import { Suspense, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import FacebookIcon from '@mui/icons-material/Facebook';
import IconButton from '@mui/material/IconButton';
import InstagramIcon from '@mui/icons-material/Instagram';
import RateReviewIcon from '@mui/icons-material/RateReview';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Outlet, useLocation } from 'react-router';

import { LocaleProvider } from '@/i18n/LocaleContext';
import {
  DEFAULT_LOCALE,
  ROUTE_SEGMENTS,
  SUPPORTED_LOCALES,
  localePrefix,
  localizedPath,
  type Locale,
} from '@/i18n/locales';
import { useBrandingQuery, useContactChannelsQuery } from '@/services/api/contentApi';

interface RootLayoutProps {
  locale: Locale;
}

const LOCALE_LABELS: Record<Locale, string> = { en: 'EN', es: 'ES' };

/** Rewrites the current path into another locale, keeping the reader in place. */
function swapLocale(pathname: string, from: Locale, to: Locale): string {
  const stripped = from === DEFAULT_LOCALE ? pathname : pathname.slice(localePrefix(from).length);
  const [segment = '', ...rest] = stripped.replace(/^\//, '').split('/');

  const key = (Object.keys(ROUTE_SEGMENTS) as (keyof typeof ROUTE_SEGMENTS)[]).find(
    (routeKey) => ROUTE_SEGMENTS[routeKey][from] === segment,
  );

  return localizedPath(key ?? 'home', to, ...rest);
}

/** Icon, label and href for one social channel, omitted whenever unconfigured. */
function socialLinks(channels: ReturnType<typeof useContactChannelsQuery>['data']) {
  if (!channels) return [];

  return [
    channels.whatsappLink && { key: 'whatsapp', label: 'WhatsApp', href: channels.whatsappLink, Icon: WhatsAppIcon },
    channels.facebook && { key: 'facebook', label: 'Facebook', href: channels.facebook, Icon: FacebookIcon },
    channels.instagram && { key: 'instagram', label: 'Instagram', href: channels.instagram, Icon: InstagramIcon },
    channels.tripadvisor && { key: 'tripadvisor', label: 'TripAdvisor', href: channels.tripadvisor, Icon: RateReviewIcon },
  ].filter((link): link is { key: string; label: string; href: string; Icon: typeof WhatsAppIcon } => Boolean(link));
}

export function RootLayout({ locale }: RootLayoutProps) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { data: channels } = useContactChannelsQuery();
  const { data: branding } = useBrandingQuery(locale);
  const links = socialLinks(channels);

  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
  }, [i18n, locale]);

  const navItems: (keyof typeof ROUTE_SEGMENTS)[] = ['fleet', 'about', 'contact'];

  return (
    <LocaleProvider value={locale}>
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        // Functions, not the palette-path string form — `custom` sits outside
        // `theme.palette`, which is all the string shorthand knows how to
        // resolve; given a string it can't place, `sx` silently drops the
        // declaration and the plain `color="default"` background shows instead.
        sx={{
          bgcolor: (theme) => theme.custom.header.background,
          color: (theme) => theme.custom.header.text,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 2 }}>
            {/* The brand wordmark and the active-language indicator both use
                `secondary.main` as a deliberate accent — independent of the header's
                own background/text pair and of whatever `primary.main` an
                administrator has picked, since that color's job elsewhere (buttons,
                the hero gradient) is unrelated to reading well as header chrome. */}
            <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
              {/* Title and Subtitle sit on one row on wide screens; on narrow
                  screens the Subtitle drops to its own line below the Title,
                  with the Slogan (if any) always last. */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 0, md: 1 }}
                sx={{ alignItems: { xs: 'flex-start', md: 'baseline' } }}
              >
                <Typography
                  component={RouterLink}
                  to={localizedPath('home', locale)}
                  variant="h6"
                  noWrap
                  sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'secondary.main', textDecoration: 'none' }}
                >
                  {branding?.title || t('site.name')}
                </Typography>
                {branding?.secondaryTitle && (
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      color: (theme) => theme.custom.header.subtitle,
                    }}
                  >
                    {branding.secondaryTitle}
                  </Typography>
                )}
              </Stack>
              {branding?.slogan && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' },
                    fontWeight: 800,
                    color: (theme) => theme.custom.header.text,
                    display: 'block',
                  }}
                >
                  {branding.slogan}
                </Typography>
              )}
            </Stack>

            {/* `color="inherit"` so these read the header's own text color
                (`custom.header.text`) instead of defaulting to `primary.main` —
                a header dark enough to need white text would otherwise render
                these invisible whenever an administrator picks a dark primary. */}
            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {navItems.map((key) => (
                <Button key={key} color="inherit" component={RouterLink} to={localizedPath(key, locale)}>
                  {t(`nav.${key}`)}
                </Button>
              ))}
            </Stack>

            <Stack direction="row" spacing={0.5} aria-label={t('nav.language')}>
              {SUPPORTED_LOCALES.map((code) => (
                <Button
                  key={code}
                  size="small"
                  component={RouterLink}
                  to={swapLocale(pathname, locale, code)}
                  variant={code === locale ? 'contained' : 'text'}
                  color={code === locale ? 'secondary' : 'inherit'}
                  aria-current={code === locale ? 'true' : undefined}
                >
                  {LOCALE_LABELS[code]}
                </Button>
              ))}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Suspense fallback={<PageSkeleton />}>
          <Outlet />
        </Suspense>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 4,
          bgcolor: (theme) => theme.custom.footer.background,
          color: (theme) => theme.custom.footer.text,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="body2">
              © {new Date().getFullYear()} {t('site.name')}. {t('footer.rights')}
            </Typography>

            {links.length > 0 && (
              <Stack direction="row" spacing={0.5} aria-label={t('footer.followUs')}>
                {links.map(({ key, label, href, Icon }) => (
                  <IconButton
                    key={key}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    size="small"
                    sx={{ color: 'inherit' }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>
    </Box>
    </LocaleProvider>
  );
}

function PageSkeleton() {
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Skeleton variant="text" height={64} width="60%" />
      <Skeleton variant="text" height={28} width="85%" />
      <Skeleton variant="rounded" height={280} sx={{ mt: 4 }} />
    </Container>
  );
}

export default RootLayout;
